'use client';

import type { ReactElement } from 'react';
import type { ShopperSearch } from '@/scapi';
import EatalyRefinePrice from '@/components/eataly-plp-filters/eataly-refine-price';
import type { FilterValue } from '@/components/eataly-plp-filters/lib/types';
import EatalyRefineCheckboxList from '@/components/eataly-plp-filters/refine-checkbox-list';
import EatalyRefineSearchableList from '@/components/eataly-plp-filters/refine-searchable-list';
import { inferFilterUiType } from '@/components/eataly-plp-filters/lib/refinement-ui-registry';
import RefineBooleanToggle from '@/components/eataly-plp-filters/refine-boolean-toggle';
import EatalyRefineCategory from '@/components/eataly-plp-filters/eataly-refine-cgid';

export type RefinementRendererProps = {
    refinement: ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] };
    isFilterSelected: (attributeId: string, value: string) => boolean;
    toggleFilter: (attributeId: string, value: string) => void;
    isPending: boolean;
};

export default function RefinementRenderer({
    refinement,
    isFilterSelected,
    toggleFilter,
    isPending,
}: RefinementRendererProps): ReactElement | null {
    const { attributeId, values } = refinement;
    const refinementProps = {
        values,
        attributeId,
        isFilterSelected,
        toggleFilter,
        isPending,
    };
    const uiType = inferFilterUiType(refinement);

    switch (uiType) {
        case 'price-slider':
            return <EatalyRefinePrice {...refinementProps} />;
        case 'boolean-toggle': {
            let toggleValue = '';
            if (attributeId === 'pmid') {
                toggleValue = values.map((value) => value.value).join('|');
            } else {
                toggleValue = values[0]?.value;
            }
            if (!toggleValue) {
                return null;
            }
            return (
                <RefineBooleanToggle
                    value={toggleValue}
                    attributeId={attributeId}
                    isFilterSelected={isFilterSelected}
                    toggleFilter={toggleFilter}
                    label={refinement.label ?? attributeId}
                    isPending={isPending}
                />
            );
        }
        case 'searchable-list':
            return <EatalyRefineSearchableList {...refinementProps} label={refinement.label ?? attributeId} />;
        case 'checkbox-list':
            return <EatalyRefineCheckboxList {...refinementProps} className="pt-4" />;
        case 'toggle-list':
            return <EatalyRefineCategory {...refinementProps} />;
        case 'skip':
            return null;
        default: {
            uiType satisfies never;
            return null;
        }
    }
}
